import { useCallback, useEffect, useState } from 'react';

// Never strand the overlay when no timeline can report back (heatmap/grid
// views, or a scheduler torn down mid-commit).
const PAINT_WAIT_MS = 5000;

/**
 * A resolved bookings request only means the data reached React — the
 * timeline still has to commit and paint it. `isPaintingEvents` takes over
 * from `isLoadingBookings` in the very render that clears it, so the two never
 * both read false before the timeline calls `handleEventsRendered`.
 */
export function useEventsPaintLatch(isLoadingBookings: boolean) {
  const [isPaintingEvents, setIsPaintingEvents] = useState(false);
  const [wasLoadingBookings, setWasLoadingBookings] = useState(isLoadingBookings);
  if (wasLoadingBookings !== isLoadingBookings) {
    setWasLoadingBookings(isLoadingBookings);
    if (wasLoadingBookings) setIsPaintingEvents(true);
  }

  useEffect(() => {
    if (!isPaintingEvents) return;
    const timeout = setTimeout(() => setIsPaintingEvents(false), PAINT_WAIT_MS);
    return () => clearTimeout(timeout);
  }, [isPaintingEvents]);

  const handleEventsRendered = useCallback(() => setIsPaintingEvents(false), []);

  return { isPaintingEvents, handleEventsRendered };
}
